import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectApp, setCurrentWatch } from "../../app/appSlice";
import "./BottomBar.css";
import { gsap } from "gsap";
import { useLocation } from "react-router-dom";
import SpatialNavigation from "spatial-navigation-js";

const BottomBar = ({ selectedEpisode }) => {
  const dispatch = useDispatch();
  const focusableContainerRef = useRef(null);
  const lastFocusedElementRef = useRef(null);
  const isInitialized = useRef(false);

  const { episode_id } = useSelector(selectApp).currentWatch.selectedEpisode;
  const {
    data,
    isProgressBarFocused,
    isEpisodesBarFocused,
    isOverlay,
    isPlayerReady
  } = useSelector(selectApp).currentWatch;

  const location = useLocation();

  const handleFocused = useCallback((event) => {
    if(!isEpisodesBarFocused) return;

    const focusedElement = event.target;
    scrollToElement(focusableContainerRef.current, focusedElement);
  },[isEpisodesBarFocused]);
  
  useEffect(() => {
    if(!isInitialized.current) {
      SpatialNavigation.add("bottombar", {
        enterTo: "last-focused", 
        disabled: !isEpisodesBarFocused,
        straightOnly: true,
        restrict: 'self-first',
        selector: `#bottombar .focusable`,
        rememberSource: true
      });
      SpatialNavigation.makeFocusable();

      isInitialized.current = true;
    }

  },[selectedEpisode?.episode_id, isEpisodesBarFocused]);

  useEffect(() => {
    if(isInitialized.current && isPlayerReady) {
      if(isOverlay && !lastFocusedElementRef.current) {
        const focusableElements = focusableContainerRef.current.querySelectorAll(".focusable");
        const currentEpisodeId = selectedEpisode?.episode_id?.toString();
        const currentEpisodeElement = Array.from(focusableElements).find(
          el => el.dataset.episodeid === currentEpisodeId
        );

        const elementToFocus =  lastFocusedElementRef.current || currentEpisodeElement || focusableElements[0];

        if (elementToFocus) {
          
          requestAnimationFrame(() => {
            const container = focusableContainerRef.current;
            const content = container.querySelector(".scroll-content");
          
            const containerWidth = container.clientWidth;
            const elementOffset = elementToFocus.offsetLeft;
            const elementWidth = elementToFocus.offsetWidth;
          
            // Center the element
            const targetX = -elementOffset + (containerWidth / 2) - (elementWidth / 2);
          
            // Clamp the scrolling range
            const minX = Math.min(0, containerWidth - content.scrollWidth);
            const clampedX = Math.max(Math.min(targetX, 0), minX);
          
            gsap.set(content, { x: clampedX });
            elementToFocus.focus();
            if(!isEpisodesBarFocused)
              elementToFocus.blur();
            lastFocusedElementRef.current = elementToFocus;
          });
        }
      }
    }
  },[isOverlay, isPlayerReady, isEpisodesBarFocused, selectedEpisode?.episode_id])

  useEffect(() => {
    if(isEpisodesBarFocused && isInitialized.current)  {
      lastFocusedElementRef.current?.focus();
      SpatialNavigation.enable("bottombar");
      focusableContainerRef.current?.addEventListener("sn:focused", handleFocused);
    }
    
    if(!isEpisodesBarFocused && isInitialized.current) {
      document.activeElement.blur();
      SpatialNavigation.disable("bottombar");      
    }
  },[isEpisodesBarFocused, handleFocused]);
  
  const handleEpisodeSelect = useCallback(
    (episode) => {
      if (episode_id.toString() !== episode.episode_id.toString() && isEpisodesBarFocused) {
        dispatch(
          setCurrentWatch({
            selectedEpisode: episode,
            isPlayerReady: false,
            isPlaying: false,
            isOverlay: false
          })
        );
      }
    },
    [dispatch, episode_id, isEpisodesBarFocused]
  );
  
  const scrollToElement = (container, targetElement) => {
    if (!container || !targetElement) return;
  
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
  
    const containerCenter = containerRect.left + containerRect.width / 2;
    const targetCenter = targetRect.left + targetRect.width / 2;
    const offset = targetCenter - containerCenter;
  
    // Get current translateX of the content
    const content = container.querySelector(".scroll-content");
    if (!content) return;
  
    const currentX = gsap.getProperty(content, "x") || 0;
    const targetX = currentX - offset;
  
    // Clamp: prevent overscrolling
    // const maxScroll = content.scrollWidth - container.clientWidth;
    const clampedX = Math.max(Math.min(targetX, 0), container.clientWidth - content.scrollWidth);

    lastFocusedElementRef.current = targetElement;
  
    gsap.to(content, {
      x: clampedX,
      duration: 0.3,
      ease: "power3.out",
      overwrite: true,
      onStart: () => {
        SpatialNavigation.disable("bottombar");
        setTimeout(() => {
          SpatialNavigation.enable("bottombar");
        },100);
      },
    });
  };

  useEffect(() => {
    if (!location.pathname.includes("watch")) {
      lastFocusedElementRef.current = null;
      isInitialized.current = false;
      focusableContainerRef.current?.removeEventListener("sn:focused", handleFocused);
      SpatialNavigation.remove("bottombar");
    }
  }, [location.pathname, handleFocused]);

  return (
    <div
      id="bottombar"
      className={`bottom-bar-container ${isProgressBarFocused ? "disabled" : ""}`}
    >
      <div
        className="spatial-section focusable-container d-flex flex-row"
        ref={focusableContainerRef}
      >
        <div className="scroll-content d-flex flex-row">
        {data?.episodes?.map((episode, index) => (  
        <div
            className={`focusable d-flex flex-column align-items-center m-2 ${
              selectedEpisode?.episode_id.toString() === episode.episode_id.toString() ? "active" : ""
            }`}
            key={index}
            data-title={episode.title}
            data-videoid={episode.video_id}
            data-episodeid={episode.episode_id}
            onClick={() => handleEpisodeSelect(episode)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.keyCode === 13) {
                handleEpisodeSelect(episode);
              }
            }}
            tabIndex={0}
          >
            {(selectedEpisode?.episode_id.toString() === episode.episode_id.toString()) && <div className="center-text"><h2>Watching…</h2></div>}
            <div className="thumbnail-wrapper">
              <img
                src={`https://i.ytimg.com/vi/${episode.video_id}/mqdefault.jpg`}
                alt={episode.episode_title}
              />
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
};

export default BottomBar;
